import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import AuthToggle from '../authentication/components/AuthToggle';
import LoginForm from '../authentication/components/LoginForm';
import RegisterForm from '../authentication/components/RegisterForm';
import TrustBadges from '../authentication/components/TrustBadges';
import UGCLayout from '../../components/layouts/UGCLayout';
import UGCBenefits from '../../components/layouts/UGCBenefits';
import { useAuth } from '../../contexts/AuthContext';

/**
 * UGC-specific Authentication Page
 * Separate from coaching authentication to prevent onboarding conflicts
 * After login/register, users go directly to UGC dashboard (never to coaching onboarding)
 */
const UGCAuthentication = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile, loading: authLoading } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  
  const mode = searchParams.get('mode');
  const email = searchParams.get('email');
  const returnUrl = searchParams.get('returnUrl');

  // If email is provided, default to register mode
  useEffect(() => {
    if (email && mode === 'register') {
      setIsLogin(false);
    }
  }, [email, mode]);

  // After auth succeeds, go to UGC dashboard or return URL
  useEffect(() => {
    if (!authLoading && user && profile) {
      // If there's a return URL, go there
      if (returnUrl) {
        navigate(returnUrl);
      } else if (profile.ugc_onboarding_completed) {
        navigate('/app/ugc-dashboard');
      } else {
        navigate('/app/ugc-onboarding');
      }
    }
  }, [user, profile, authLoading, navigate, returnUrl]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-amber-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-amber-100">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <UGCLayout>
      <div className="p-4 min-h-screen flex items-center justify-center">
        <div className="w-full max-w-4xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left side - Branding & Benefits */}
            <div className="hidden md:block text-white">
              <div className="mb-12">
                <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-amber-300 via-orange-400 to-amber-500 bg-clip-text text-transparent">
                  Join the UGC Revolution
                </h1>
                <p className="text-lg text-amber-100/80 leading-relaxed">
                  Get paid to create authentic product reviews. Join thousands of creators earning real money on their own schedule.
                </p>
              </div>

              <UGCBenefits />
            </div>

            {/* Right side - Auth Form */}
            <div className="w-full max-w-md mx-auto">
              <div className="bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl">
                {/* Header */}
                <div className="mb-8 text-center">
                  <div className="inline-block w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                    <Icon name="Sparkles" size={28} color="white" />
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-2">
                    {isLogin ? 'Welcome Back' : 'Get Started'}
                  </h2>
                  <p className="text-amber-100/60">
                    {isLogin ? 'Access your UGC dashboard' : 'Join our creator community'}
                  </p>
                </div>

                {/* Auth Toggle */}
                <div className="mb-8">
                  <AuthToggle
                    isLogin={isLogin}
                    onToggle={() => setIsLogin(!isLogin)}
                  />
                </div>

                {/* Forms */}
                {isLogin ? (
                  <LoginForm 
                    source="ugc"
                    onSuccess={() => {
                      // Navigation handled by useEffect
                    }}
                  />
                ) : (
                  <RegisterForm
                    prefillEmail={email}
                    source="ugc"
                    onSuccess={() => {
                      // Navigation handled by useEffect
                    }}
                  />
                )}

                {/* Trust Badges */}
                <div className="mt-8 pt-8 border-t border-white/10">
                  <TrustBadges />
                </div>
              </div>

              {/* Mobile benefits - shown on small screens */}
              <div className="md:hidden mt-8 text-center text-amber-100">
                <p className="text-sm mb-4">
                  ✨ Start earning today as a UGC creator ✨
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </UGCLayout>
  );
};

export default UGCAuthentication;
